import { z } from "zod";
import { exportFileFormat } from "./export-format";
import {
  MAX_GRID_ATTRIBUTES,
  MAX_GRID_OPTIONS,
  MAX_PRICE_CENTS,
  MAX_STOCK_QTY_Q4,
  gridIssues,
  isValidCest,
  isValidGtin,
  isValidNcm,
  normalizeGrid,
  type GridAttribute,
  type VariationValue,
} from "@cx/domain";
import { listQuery } from "./common";
import { MAX_CUSTOM_FIELD_VALUES, httpUrl, isoDateValue } from "./contacts";
import { blank, num, text } from "./fields";
import { ulidParam } from "./ids";

/** Imagens por produto (RF-CAD-02, aba Imagens). */
export const MAX_PRODUCT_IMAGES = 20;
export const MAX_PRODUCT_IMAGE_BYTES = 10 * 1024 * 1024;
export const PRODUCT_IMAGE_MIME = ["image/png", "image/jpeg", "image/webp"] as const;
/** Fornecedores por produto. */
export const MAX_PRODUCT_SUPPLIERS = 50;
/** Componentes por kit (aba Estrutura). */
export const MAX_KIT_COMPONENTS = 50;
/** S simples | V com variações (pai) | E com composição (kit). O filho de variação é S com `parentId`. */
export const PRODUCT_FORMATS = ["S", "V", "E"] as const;
export type ProductFormat = (typeof PRODUCT_FORMATS)[number];

/**
 * Colunas que o filho com "Utilizar informações do produto pai" recebe do pai (além da aba Tributação inteira): os
 * grupos Dimensões e peso e Descrição e links da tela. Preço, SKU, GTIN, situação e estoque são do filho.
 */
export const PRODUCT_INHERITED_FIELDS = [
  "netWeightG",
  "grossWeightG",
  "widthMm",
  "heightMm",
  "depthMm",
  "dimensionUnit",
  "volumes",
  "itemsPerBox",
  "shortDescription",
  "longDescription",
  "externalLink",
  "videoUrl",
  "notes",
] as const;

const cents = num(
  z
    .number()
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite"),
);
const q4 = num(
  z
    .number()
    .int("Quantidade inválida")
    .min(0, "Quantidade não pode ser negativa")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
);
const posInt = (max: number) => num(z.number().int().min(0).max(max));
const date = blank.pipe(isoDateValue.nullable());

/** Só dígitos depois de tirar pontos, traços e espaços; valida o tamanho com `check`. */
const digitsCode = (check: (s: string) => boolean, message: string) =>
  blank.transform((v, ctx) => {
    if (v === null) return null;
    const d = v.replace(/[.\-\s]/g, "");
    if (!check(d)) {
      ctx.addIssue({ code: "custom", message });
      return z.NEVER;
    }
    return d;
  });

/** GTIN/EAN com dígito verificador, ou "SEM GTIN" (valor aceito pela NF-e para produto sem código de barras). */
const gtin = blank.transform((v, ctx) => {
  if (v === null) return null;
  const up = v.toUpperCase();
  if (up === "SEM GTIN") return up;
  if (!isValidGtin(up)) {
    ctx.addIssue({ code: "custom", message: "GTIN inválido (confira o dígito verificador)" });
    return z.NEVER;
  }
  return up;
});

/** Tipo do item no SPED (registro 0200). */
export const SPED_ITEM_TYPES = [
  "00",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "99",
] as const;

export const productTaxInput = z.object({
  origin: z.number().int().min(0, "Origem 0 a 8").max(8, "Origem 0 a 8").default(0),
  ncm: digitsCode(isValidNcm, "NCM deve ter 8 dígitos"),
  cest: digitsCode(isValidCest, "CEST deve ter 7 dígitos"),
  exTipi: blank.pipe(
    z
      .string()
      .regex(/^\d{1,3}$/, "EX TIPI tem até 3 dígitos")
      .nullable(),
  ),
  fciNumber: blank.pipe(
    z
      .string()
      .toUpperCase()
      .regex(/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/, "Número da FCI inválido")
      .nullable(),
  ),
  spedItemType: z
    .enum(SPED_ITEM_TYPES, "Tipo do item inválido")
    .nullish()
    .transform((v) => v ?? null),
  serviceListCode: blank.pipe(
    z
      .string()
      .regex(/^\d{1,2}\.\d{2}$/, "Use o formato da LC 116, ex.: 01.07")
      .nullable(),
  ),
  ipiClass: blank.pipe(
    z
      .string()
      .regex(/^\d{3}$/, "Código de enquadramento do IPI tem 3 dígitos")
      .nullable(),
  ),
  ipiSealCode: text(60),
  ipiFixedCents: cents,
  pisFixedCents: cents,
  cofinsFixedCents: cents,
  icmsStBaseRetCents: cents,
  icmsStRetCents: cents,
  icmsOwnSubstituteCents: cents,
  ibsCbsClass: blank.pipe(
    z
      .string()
      .regex(/^\d{6}$/, "Classificação tributária (cClassTrib) tem 6 dígitos")
      .nullable(),
  ),
  /** % aproximado de tributos (Lei 12.741) em pct4: 0 a 100%. */
  approxTaxPct4: num(z.number().int().min(0).max(1_000_000, "Percentual até 100%")),
  extraInfo: text(500),
});
export type ProductTaxInput = z.output<typeof productTaxInput>;

export const productSupplierInput = z.object({
  supplierId: ulidParam,
  supplierCode: text(60),
  supplierDescription: text(120),
  lastCostCents: cents,
  isDefault: z.boolean().default(false),
});
export type ProductSupplierInput = z.output<typeof productSupplierInput>;

export const productComponentInput = z.object({
  componentId: ulidParam,
  /** Quantidade do componente em um kit (q4), maior que zero. */
  qtyQ4: z
    .number("Informe a quantidade")
    .int("Quantidade inválida")
    .positive("Quantidade deve ser maior que zero"),
});
export type ProductComponentInput = z.output<typeof productComponentInput>;

export const productInput = z
  .object({
    format: z.enum(PRODUCT_FORMATS, "Formato inválido").default("S"),
    kind: z.enum(["P", "S"]).default("P"),
    name: z.string().trim().min(1, "Informe o nome").max(120),
    sku: text(60),
    status: z.enum(["active", "inactive"]).default("active"),
    priceCents: cents.transform((v) => v ?? 0),
    costCents: cents.transform((v) => v ?? 0),
    unit: z
      .string()
      .trim()
      .min(1, "Informe a unidade")
      .max(6, "Unidade com até 6 letras")
      .toUpperCase()
      .default("UN"),
    condition: z.enum(["unspecified", "new", "used", "refurbished"]).default("unspecified"),
    productLine: text(60),
    production: z.enum(["own", "third_party"]).default("own"),
    expirationDate: date,
    freeShipping: z.boolean().default(false),
    netWeightG: posInt(100_000_000),
    grossWeightG: posInt(100_000_000),
    widthMm: posInt(100_000_000),
    heightMm: posInt(100_000_000),
    depthMm: posInt(100_000_000),
    dimensionUnit: z.enum(["m", "cm", "mm"]).default("cm"),
    volumes: posInt(100_000),
    itemsPerBox: posInt(1_000_000),
    gtin,
    gtinTax: gtin,
    shortDescription: text(255),
    longDescription: z
      .string()
      .max(10_000)
      .nullish()
      .transform((v) => (v?.trim() ? v : null)),
    externalLink: blank.pipe(httpUrl.nullable()),
    videoUrl: blank.pipe(httpUrl.nullable()),
    notes: z
      .string()
      .max(5000)
      .nullish()
      .transform((v) => (v?.trim() ? v : null)),
    stockMinQ4: q4,
    stockMaxQ4: q4,
    crossdockingDays: posInt(365),
    location: text(60),
    batchControl: z.boolean().default(false),
    stockControl: z.boolean().default(true),
    tax: productTaxInput.prefault({}),
    suppliers: z.array(productSupplierInput).max(MAX_PRODUCT_SUPPLIERS).default([]),
    /** Só no kit (formato E). */
    components: z.array(productComponentInput).max(MAX_KIT_COMPONENTS).default([]),
    /** Só no filho de variação: "Utilizar informações do produto pai". */
    inheritParent: z.boolean().default(true),
    customFields: z
      .record(ulidParam, z.string().max(2000))
      .refine((r) => Object.keys(r).length <= MAX_CUSTOM_FIELD_VALUES, {
        message: `No máximo ${MAX_CUSTOM_FIELD_VALUES} campos customizados.`,
      })
      .default({}),
  })
  .superRefine((v, ctx) => {
    if (v.suppliers.filter((s) => s.isDefault).length > 1)
      ctx.addIssue({ code: "custom", path: ["suppliers"], message: "Marque só um fornecedor padrão" });
    if (new Set(v.suppliers.map((s) => s.supplierId)).size !== v.suppliers.length)
      ctx.addIssue({ code: "custom", path: ["suppliers"], message: "Fornecedor repetido" });
    if (v.format === "E" && v.components.length === 0)
      ctx.addIssue({ code: "custom", path: ["components"], message: "Inclua ao menos um componente no kit" });
    if (v.format !== "E" && v.components.length > 0)
      ctx.addIssue({
        code: "custom",
        path: ["components"],
        message: "Só produto com composição tem componentes",
      });
    if (new Set(v.components.map((c) => c.componentId)).size !== v.components.length)
      ctx.addIssue({ code: "custom", path: ["components"], message: "Componente repetido" });
    if (v.stockMinQ4 != null && v.stockMaxQ4 != null && v.stockMinQ4 > v.stockMaxQ4)
      ctx.addIssue({
        code: "custom",
        path: ["stockMinQ4"],
        message: "Estoque mínimo maior que o máximo",
      });
  });

export type ProductInput = z.output<typeof productInput>;
export type ProductFormValues = z.input<typeof productInput>;

export const productListQuery = listQuery.extend({
  status: z.enum(["active", "inactive"]).optional(),
  kind: z.enum(["P", "S"]).optional(),
  format: z.enum(PRODUCT_FORMATS).optional(),
  /** Variações (filhos) ficam fora da lista, salvo com "yes" (seletores de componente, por exemplo). */
  includeVariations: z.enum(["yes", "no"]).optional(),
  hasImage: z.enum(["yes", "no"]).optional(),
  hasNcm: z.enum(["yes", "no"]).optional(),
  /** "recent" = Últimos incluídos (ULID decrescente). */
  sort: z.enum(["name", "recent"]).default("name"),
});
export type ProductListQuery = z.output<typeof productListQuery>;

export const productExportInput = productListQuery
  .pick({
    q: true,
    status: true,
    kind: true,
    format: true,
    includeVariations: true,
    hasImage: true,
    hasNcm: true,
    sort: true,
  })
  .extend({ fileFormat: exportFileFormat });
export type ProductExportInput = z.output<typeof productExportInput>;

/** Imagem por endereço externo: só http/https com domínio de verdade. */
export const productImageUrlInput = z.object({
  url: z
    .url({
      protocol: /^https?$/,
      hostname: z.regexes.domain,
      error: "Endereço inválido (use http:// ou https://)",
    })
    .max(1000),
});
export const productImageOrderInput = z.object({
  ids: z
    .array(ulidParam)
    .min(1)
    .max(MAX_PRODUCT_IMAGES)
    .refine((ids) => new Set(ids).size === ids.length, "Imagem repetida"),
});

/** Grade: atributos com opções. A validação e a arrumação são as do domínio (`normalizeGrid`). */
export const variationGenerateInput = z
  .object({
    // Os limites do domínio também no schema: corta antes de validar (e normalizar) uma grade enorme.
    attributes: z
      .array(
        z.object({
          name: z.string().max(200),
          options: z
            .array(z.string().max(200))
            .max(MAX_GRID_OPTIONS, `No máximo ${MAX_GRID_OPTIONS} opções por atributo`),
        }),
      )
      .max(MAX_GRID_ATTRIBUTES, `No máximo ${MAX_GRID_ATTRIBUTES} atributos`),
  })
  .transform((v, ctx) => {
    const issues = gridIssues(v.attributes);
    if (issues.length) {
      for (const i of issues)
        ctx.addIssue({ code: "custom", path: ["attributes", ...i.path], message: i.message });
      return z.NEVER;
    }
    return { attributes: normalizeGrid(v.attributes) };
  });
export type VariationGenerateInput = z.output<typeof variationGenerateInput>;
export type VariationGenerateResult = {
  /** Ids dos filhos criados agora, na ordem da grade. */
  created: string[];
  /** Combinações da grade que já tinham filho. */
  existing: number;
  /** Filhos ativos cuja combinação não está na grade nova (não são excluídos). */
  outOfGrid: number;
};

export type ProductListItem = {
  id: string;
  /** Pai, quando a linha é uma variação. */
  parentId: string | null;
  /** Variações ativas do pai (formato V); 0 nos demais. */
  variationCount: number;
  sku: string | null;
  name: string;
  kind: "P" | "S";
  format: "S" | "V" | "E";
  status: "active" | "inactive";
  unit: string;
  priceCents: number;
  /** Ausente para quem não tem products:read_cost. */
  costCents?: number;
  gtin: string | null;
  ncm: string | null;
  /** Miniatura: URL assinada curta (imagem no R2) ou null. */
  imageUrl: string | null;
};
/** Os campos de custo (`costCents`, `avgCostCents`, `lastCostCents`) só vêm para quem tem products:read_cost. */
export type ProductSupplierView = Omit<ProductSupplierInput, "lastCostCents"> & {
  lastCostCents?: number | null;
  supplierName: string;
};
export type ProductComponentView = ProductComponentInput & {
  name: string;
  sku: string | null;
  unit: string;
  status: "active" | "inactive";
  /** Ausente sem products:read_cost. */
  costCents?: number;
  stockControl: boolean;
};
export type ProductVariationView = {
  id: string;
  name: string;
  sku: string | null;
  status: "active" | "inactive";
  priceCents: number;
  values: VariationValue[];
  /** false quando a combinação saiu da grade atual do pai. */
  inGrid: boolean;
};
export type ProductDetail = Omit<ProductInput, "suppliers" | "components" | "costCents"> & {
  id: string;
  createdAt: string;
  updatedAt: string;
  costCents?: number;
  /** Custo médio ponderado, calculado pelo estoque (T1.3); só leitura. */
  avgCostCents?: number;
  suppliers: ProductSupplierView[];
  /** Variação: pai e valores da combinação. */
  parentId: string | null;
  parent: { id: string; name: string; sku: string | null } | null;
  variationValues: VariationValue[];
  /** Pai (formato V): grade e filhos ativos. */
  attributes: GridAttribute[];
  variations: ProductVariationView[];
  /** Kit (formato E): componentes, custo e disponível derivado (null = sem limite). */
  components: ProductComponentView[];
  /** `componentsCostCents` ausente sem products:read_cost. */
  kit: { componentsCostCents?: number; availableQ4: number | null } | null;
};
export type ProductImageView = {
  id: string;
  /** URL assinada (R2) ou o endereço externo. */
  url: string;
  external: boolean;
  fileName: string | null;
  mime: string | null;
  sizeBytes: number | null;
  position: number;
};
